import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import PostCard, { THREE_COLUMNS_UP } from '../../components/blog/PostCard';
import posts from '../../data/posts.json';
import PageMeta from '../../components/common/PageMeta';
import { pagePosts } from '../../common/blogPaging';

// Internal sentinel for "no category filter" — stable across locales,
// distinct from its translated display label (common.view_all).
const ALL = '__all__';

const categoryOptions = t => [
  { value: ALL, label: t('common.view_all') },
  ...[...new Set(posts.map(post => post.category).filter(Boolean))].map(c => ({ value: c, label: c })),
];

// Category filter as a row of chips above the grid, on every screen size. It
// replaced a sidebar menu whose ~236px held the grid to two wide columns.
const CategoryChips = ({ options, active, onSelect }) => {
  const { t } = useTranslation();
  return (
    <Box component="nav" aria-label={t('blog.categories_label')} sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: { xs: 4, md: 5 } }}>
      {options.map(option => (
        <Chip
          key={option.value}
          label={option.label}
          clickable
          onClick={() => onSelect(option.value)}
          color={option.value === active ? 'primary' : 'default'}
          variant={option.value === active ? 'filled' : 'outlined'}
          aria-current={option.value === active ? 'true' : undefined}
          sx={{ fontWeight: 600 }}
        />
      ))}
    </Box>
  );
};

// One grid for every width — 1 column, 2 from sm (600px), 3 from
// THREE_COLUMNS_UP — with the featured post as its first item, spanning two
// columns once there are three. pagePosts sizes each load so rows stay full.
const Blog = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get('category') || ALL;
  const requestedPage = Math.max(1, parseInt(searchParams.get('page')) || 1);
  const options = categoryOptions(t);

  const filtered = category === ALL ? posts : posts.filter(post => post.category === category);
  const { featured, rest, page, hasMore } = pagePosts(filtered, requestedPage);
  const isFiltered = requestedPage > 1 || category !== ALL;

  // "Load more" replaces the history entry rather than pushing one, so Back
  // leaves the blog instead of un-loading a batch — and returning from a post
  // restores the posts that were loaded.
  const update = (nextCategory, nextPage) => {
    const params = {};
    if (nextCategory !== ALL)
      params.category = nextCategory;
    if (nextPage > 1)
      params.page = String(nextPage);
    setSearchParams(params, { replace: nextPage > 1 });
  };

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
      <PageMeta title={t('blog.eyebrow')} description={t('blog.meta_description')} robots={isFiltered ? 'noindex, follow' : undefined} />
      <Box sx={{ textAlign: 'center', mx: 'auto', mb: 4 }}>
        <Typography sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
          {t('blog.eyebrow')}
        </Typography>
        <Typography variant="h2" component="h1" sx={{ color: 'surface.dark', fontSize: { xs: '2.25rem', md: '3rem' } }}>
          {t('blog.heading')}
        </Typography>
      </Box>
      <CategoryChips options={options} active={category} onSelect={c => update(c, 1)} />
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, [THREE_COLUMNS_UP]: { gridTemplateColumns: 'repeat(3, 1fr)' }, gap: 3 }}>
        {featured && (
          <Box sx={{ display: 'grid', [THREE_COLUMNS_UP]: { gridColumn: 'span 2' } }}>
            <PostCard post={featured} featured />
          </Box>
        )}
        {rest.map(post => <PostCard key={post.slug} post={post} />)}
      </Box>
      {filtered.length === 0 && (
        <Typography sx={{ color: 'surface.contrastText', textAlign: 'center', py: 6 }}>
          {t('blog.empty')}
        </Typography>
      )}
      {hasMore && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mt: 5 }}>
          <Typography variant="body2" sx={{ color: 'surface.contrastText' }}>
            {t('blog.showing', { shown: 1 + rest.length, total: filtered.length })}
          </Typography>
          <Button variant="outlined" onClick={() => update(category, page + 1)}>
            {t('common.load_more')}
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default Blog;
